"use client";

import { motion } from "framer-motion";
import {
  Play,
  ArrowRight,
  Check,
  MessageCircle,
} from "lucide-react";
import { fadeUp, scaleIn, Section, img, agents } from "./utils";
import { VideoModal } from "../ui/video-modal";

export function Hero() {
  return (
    <Section className="relative overflow-hidden px-4 sm:px-6 pt-32 pb-8 lg:pt-40 lg:pb-16 max-w-full">
      {/* Background: real Dubai skyline, blurred + darkened */}
      <div className="absolute inset-0 z-0">
        <img
          src={img.dubaiSkyline}
          alt=""
          className="w-full h-full object-cover opacity-[0.07]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-surface via-surface/80 to-surface" />
      </div>

      {/* Radial accent */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] rounded-full blur-[120px]"
          style={{
            background:
              "radial-gradient(circle, rgba(222,115,86,0.08) 0%, transparent 70%)",
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto overflow-hidden">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left — copy */}
          <div>
            <motion.div
              variants={fadeUp}
              custom={0}
              className="inline-flex items-center gap-2.5 border border-surface-border bg-surface-secondary/50 backdrop-blur-sm rounded-full px-4 py-2 mb-6"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ok opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-ok" />
              </span>
              <span className="text-sm text-content-secondary font-medium">
                47 tasks completed today across 12 teams
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              custom={1}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-content leading-[1.1]"
            >
              Your AI crew that
              <br />
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(to right, #DE7356, #E8917A, #FAFAFA)",
                }}
              >
                runs your operations.
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              custom={2}
              className="mt-5 text-base sm:text-lg text-content-secondary max-w-lg leading-relaxed"
            >
              Tethra gives you a team of AI employees that manage your
              pipeline, coordinate handoffs, and work your deals — 24/7,
              without scripts or rules engines. Built for teams that close
              deals, not write code.
            </motion.p>

            <motion.div
              variants={fadeUp}
              custom={3}
              className="mt-8 flex items-center gap-3"
            >
              <a
                href="/signup"
                className="group bg-coral-500 hover:bg-coral-400 text-white font-semibold rounded-full px-6 py-2.5 text-sm transition-all duration-300 active:scale-95 shadow-lg shadow-coral-500/25 hover:shadow-xl hover:shadow-coral-500/40"
              >
                Start Free Trial
                <ArrowRight
                  size={14}
                  className="inline ml-1.5 group-hover:translate-x-0.5 transition-transform"
                />
              </a>
              <VideoModal>
                <button className="group flex items-center gap-2 border border-surface-hover hover:border-content-muted text-content font-medium rounded-full px-5 py-2.5 text-sm transition-all duration-300 active:scale-95 hover:bg-surface-secondary/50 cursor-pointer">
                  <Play size={14} className="text-coral-400" />
                  Watch Demo
                </button>
              </VideoModal>
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={4}
              className="mt-8 flex items-center gap-4"
            >
              <div className="flex -space-x-3">
                {agents.map((a, i) => (
                  <img
                    key={i}
                    src={a.avatar}
                    alt={a.name}
                    className="w-10 h-10 rounded-full border-2 border-surface object-cover"
                  />
                ))}
              </div>
              <div className="text-sm">
                <span className="text-content font-medium">200+ teams</span>
                <span className="text-content-muted">
                  {" "}
                  across Dubai, Abu Dhabi, and Riyadh
                </span>
              </div>
            </motion.div>

            <motion.p
              variants={fadeUp}
              custom={5}
              className="mt-3 text-xs text-content-muted"
            >
              No credit card required. Cancel anytime.
            </motion.p>
          </div>

          {/* Right — dashboard mockup (hidden on mobile) */}
          <motion.div variants={scaleIn} custom={2} className="relative group hidden lg:block">
            {/* Neon snake border */}
            <div className="absolute -inset-[2px] rounded-[18px] overflow-hidden z-0 pointer-events-none">
              <div
                className="absolute top-1/2 left-1/2 w-[200%] h-[200%] -translate-x-1/2 -translate-y-1/2 animate-neon-snake"
                style={{
                  background:
                    "conic-gradient(from 0deg, transparent 0%, transparent 60%, #DE735640 65%, #DE7356 78%, #E8917A 85%, #DE7356 92%, #DE735640 95%, transparent 100%)",
                }}
              />
              <div className="absolute inset-[2px] rounded-2xl bg-surface" />
            </div>
            <div className="relative rounded-2xl overflow-hidden bg-surface-secondary/60 backdrop-blur shadow-2xl shadow-black/40 z-[1]">
              {/* Fake browser chrome */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-surface-border bg-surface-secondary/80">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-surface-hover" />
                  <div className="w-3 h-3 rounded-full bg-surface-hover" />
                  <div className="w-3 h-3 rounded-full bg-surface-hover" />
                </div>
                <div className="flex-1 mx-4 bg-surface-tertiary rounded-md h-6 flex items-center px-3">
                  <span className="text-[11px] text-content-muted">
                    app.tethra.ai/dashboard
                  </span>
                </div>
              </div>

              {/* Dashboard content */}
              <div className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-content-muted">Good morning, Muneer</p>
                    <p className="text-sm font-semibold text-content">
                      Pipeline Overview
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {["Today", "Week", "Month"].map((t) => (
                      <span
                        key={t}
                        className={`text-[10px] px-2.5 py-1 rounded-full ${
                          t === "Week"
                            ? "bg-coral-500/20 text-coral-400"
                            : "bg-surface-tertiary text-content-muted"
                        }`}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Active Deals", val: "47", change: "+12%", up: true },
                    { label: "Tasks Done", val: "182", change: "+34%", up: true },
                    { label: "Revenue MTD", val: "AED 1.2M", change: "+23%", up: true },
                  ].map((s, i) => (
                    <div
                      key={i}
                      className="bg-surface-tertiary/50 rounded-xl p-3 border border-surface-hover/40"
                    >
                      <p className="text-[10px] text-content-muted">{s.label}</p>
                      <p className="text-lg font-bold text-content mt-1">
                        {s.val}
                      </p>
                      <p
                        className={`text-[10px] mt-1 ${
                          s.up ? "text-ok" : "text-danger"
                        }`}
                      >
                        {s.change} this week
                      </p>
                    </div>
                  ))}
                </div>

                {/* Mini pipeline kanban */}
                <div className="grid grid-cols-4 gap-2">
                  {["New Lead", "Qualified", "Viewing", "Closed"].map(
                    (stage, i) => (
                      <div key={stage}>
                        <p className="text-[9px] text-content-muted uppercase tracking-wider mb-2">
                          {stage}
                        </p>
                        {[...Array(3 - i)].map((_, j) => (
                          <div
                            key={j}
                            className="bg-surface-tertiary/80 rounded-lg p-2 mb-1.5 border border-surface-hover/30"
                          >
                            <div className="flex items-center gap-1.5">
                              <div className="w-4 h-4 rounded-full bg-surface-hover" />
                              <div className="w-12 h-2 rounded bg-surface-hover" />
                            </div>
                          </div>
                        ))}
                        {i === 3 && (
                          <div className="bg-ok/10 border border-ok/20 rounded-lg p-2 mb-1.5">
                            <div className="flex items-center gap-1.5">
                              <Check size={10} className="text-ok" />
                              <span className="text-[9px] text-ok">
                                AED 85K
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>

                {/* Live agent activity */}
                <div className="bg-surface-tertiary/30 border border-surface-hover/30 rounded-xl p-3">
                  <p className="text-[10px] text-content-muted uppercase tracking-wider mb-2">
                    Live Agent Activity
                  </p>
                  {[
                    { agent: agents[0], action: "Matched 3 listings for Ahmed, booked 2 viewings", time: "2m ago" },
                    { agent: agents[1], action: "Prepped docs and moved 'Marina Tower' to Viewing", time: "5m ago" },
                    { agent: agents[2], action: "Sent post-viewing follow-up to client", time: "8m ago" },
                  ].map((a, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2.5 py-1.5 border-b border-surface-border/50 last:border-0"
                    >
                      <img
                        src={a.agent.avatar}
                        alt={a.agent.name}
                        className="w-5 h-5 rounded-full object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] text-content/80 truncate">
                          <span className="font-medium">{a.agent.name}</span>{" "}
                          {a.action}
                        </p>
                      </div>
                      <span className="text-[9px] text-content-muted shrink-0">
                        {a.time}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating WhatsApp notification */}
            <motion.div
              initial={{ opacity: 0, x: 20, y: 10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ delay: 1.5, duration: 0.5 }}
              className="absolute -bottom-4 -left-4 sm:-left-8 z-[2] bg-surface-secondary border border-surface-hover rounded-2xl p-3 shadow-xl shadow-black/40 max-w-[220px]"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-6 h-6 rounded-full bg-ok flex items-center justify-center">
                  <MessageCircle size={12} className="text-white" />
                </div>
                <span className="text-[10px] text-content-secondary">
                  WhatsApp · just now
                </span>
              </div>
              <p className="text-[11px] text-content/80 leading-snug">
                <span className="font-medium text-coral-400">Sara:</span>{" "}
                Qualified Ahmed (AED 1.8M). Matched 3 listings in JBR.
                Booking viewings and looping Omar for docs.
              </p>
            </motion.div>
          </motion.div>
        </div>

        {/* Social proof bar — logos */}
        <motion.div
          variants={fadeUp}
          custom={6}
          className="mt-20 pt-10 border-t border-surface-border/50"
        >
          <p className="text-sm text-content-muted text-center mb-6">
            Trusted by 200+ teams across the Gulf
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
            {[
              "Darwish Realty",
              "DriveHub Fleet",
              "Gulf Realty Co.",
              "PropVista",
              "Saha Properties",
            ].map((name) => (
              <span
                key={name}
                className="text-sm font-medium text-content-muted/60 tracking-wide"
              >
                {name}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </Section>
  );
}
